import {
	createFileRoute,
	stripSearchParams,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { cursorToOffset, offsetToCursor } from "graphql-relay";
import { useEffect, useTransition } from "react";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";
import z from "zod";
import { WorkflowsTable } from "@/components/tables/workflows";
import type { workflowsPageFragment$key } from "./__generated__/workflowsPageFragment.graphql";
import type { workflowsQuery } from "./__generated__/workflowsQuery.graphql";
import type { workflowsTableQuery } from "./__generated__/workflowsTableQuery.graphql";

const defaultValues = {
	page: 1,
} as const;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/automacoes/workflows",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			page: z.number().min(1).default(1),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { page } = useSearch({ from: Route.id });
	const [isPending, startTransition] = useTransition();
	const navigate = useNavigate({
		from: Route.fullPath,
	});
	const data = useLazyLoadQuery<workflowsQuery>(
		graphql`
    query workflowsQuery($slug: String!, $first: Int, $last: Int, $after: String, $before: String) {
      club(slug: $slug) {
          ...workflowsPageFragment @arguments(first: $first, last: $last, after: $after, before: $before)
      }
    }
  `,
		{
			slug: club?.slug,
			first: 15,
			after: page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
		},
	);

	const [workflows, refetch] = useRefetchableFragment<
		workflowsTableQuery,
		workflowsPageFragment$key
	>(
		graphql`
    fragment workflowsPageFragment on Club
        @refetchable(queryName: "workflowsTableQuery")
        @argumentDefinitions(
          first: { type: "Int"}
          last: { type: "Int"}
          after: { type: "String"}
          before: { type: "String"}
        )
    {
        workflows(first: $first, after: $after, before: $before, last: $last) {
          edges {
            node {
              id
              name
              active
              createdAt
              updatedAt
             	createdBy {
                displayName
               	avatarFallback
               	photo {
                  signedUrl
                }
              }
            }
            cursor
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            totalCount
            startCursor
            endCursor
          }
        }
    }
  `,
		data.club,
	);

	useEffect(() => {
		startTransition(() => {
			refetch(
				{
					first: 15,
					after: page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
				},
				{ fetchPolicy: "store-or-network" },
			);
		});
	}, [page, refetch]);
	const refreshWorkflows = () => {
		startTransition(() => {
			refetch({}, { fetchPolicy: "network-only" });
		});
	};

	return (
		<div className="p-6">
			<WorkflowsTable
				data={
					workflows?.workflows?.edges
						.filter((e) => Boolean(e?.node))
						.map(({ node }) => node) || []
				}
				isPending={isPending}
				onDeleted={refreshWorkflows}
				totalCount={workflows?.workflows.pageInfo.totalCount || 0}
				hasNextPage={Boolean(workflows?.workflows.pageInfo.hasNextPage)}
				hasPreviousPage={Boolean(workflows?.workflows.pageInfo.hasPreviousPage)}
				loadNext={() =>
					startTransition(() => {
						navigate({ search: (s) => ({ ...s, page: s.page + 1 }) });
					})
				}
				loadPrevious={() =>
					startTransition(() => {
						navigate({ search: (s) => ({ ...s, page: s.page - 1 }) });
					})
				}
				startIndex={
					workflows?.workflows.edges[0]
						? cursorToOffset(workflows?.workflows.edges[0].cursor) + 1
						: 1
				}
				endIndex={
					workflows?.workflows.edges[workflows?.workflows.edges.length - 1]
						? cursorToOffset(
								workflows?.workflows.edges[
									workflows?.workflows.edges.length - 1
								].cursor,
							) + 1
						: 1
				}
			/>
		</div>
	);
}
